import { supabase } from '@/lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// 📝 TYPES
// ============================================

export type Matiere = 'CEJM' | 'Gestion Opérationnelle';
export type CEJMSousMatiere = 'Economie' | 'Droit' | 'Management';

export interface FicheItem {
  id: string;
  title: string;
  matiere: Matiere;
  theme_id: string | null;
  sous_matiere: CEJMSousMatiere | null;
  chapitre_id: string | null;
  markdown_url: string;
  ordre: number;
  is_free: boolean;
}

export interface VideoItem {
  id: string;
  title: string;
  matiere: Matiere;
  theme_id: string | null;
  sous_matiere: CEJMSousMatiere | null;
  chapitre_id: string | null;
  duration: string;
  thumbnail_url: string | null;
  video_url: string;
  ordre: number;
  is_free: boolean;
}

export interface QuizItem {
  id: string;
  title: string;
  matiere: Matiere;
  theme_id: string | null;
  sous_matiere: CEJMSousMatiere | null;
  chapitre_id: string | null;
  questions: {
    question: string;
    answers: string[];
    correctIndex?: number;
    correctIndexes?: number[];
    type?: 'single' | 'multiple';
    explanation?: string;
  }[];
  ordre: number;
  is_free: boolean;
}

export interface ExamenItem {
  id: string;
  title: string;
  matiere: Matiere;
  year: number;
  pdf_url: string;
  corrige_url: string | null;
  ordre: number;
  is_free: boolean;
}

export type ContentType = 'fiches' | 'videos' | 'quiz' | 'examens';

// ============================================
// 📦 CACHE MÉMOIRE (instantané) + AsyncStorage (persistant)
// ============================================

const CACHE_KEY = 'prof_kylian_content_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

interface CachedContent {
  fiches: FicheItem[];
  videos: VideoItem[];
  quiz: QuizItem[];
  examens: ExamenItem[];
  timestamp: number;
}

// ✅ CACHE MÉMOIRE GLOBAL - instantané, persiste entre navigations
let memoryCache: CachedContent | null = null;

// Lecture depuis mémoire (SYNCHRONE = instantané)
const getMemoryCache = (): CachedContent | null => {
  if (!memoryCache) return null;
  return memoryCache;
};

// Sauvegarde en mémoire (SYNCHRONE)
const setMemoryCache = (data: Omit<CachedContent, 'timestamp'>): void => {
  memoryCache = {
    ...data,
    timestamp: Date.now(),
  };
};

// Lecture depuis AsyncStorage (pour restaurer au démarrage)
const getAsyncCache = async (): Promise<CachedContent | null> => {
  try {
    const cached = await AsyncStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const data: CachedContent = JSON.parse(cached);
    return data;
  } catch {
    return null;
  }
};

// Sauvegarde dans AsyncStorage (pour persistance)
const setAsyncCache = async (data: Omit<CachedContent, 'timestamp'>): Promise<void> => {
  try {
    const cacheData: CachedContent = {
      ...data,
      timestamp: Date.now(),
    };
    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('[Cache] Error saving:', error);
  }
};

// ============================================
// 📚 CONSTANTES (structure référentiel)
// ============================================

export const CEJM_THEMES = [
  { id: 'theme-1', code: 'IEE', name: "L'intégration de l'entreprise dans son environnement" },
  { id: 'theme-2', code: 'RAE', name: "La régulation de l'activité économique" },
  { id: 'theme-3', code: 'OAE', name: "L'organisation de l'activité de l'entreprise" },
  { id: 'theme-4', code: 'INVE', name: "L'impact du numérique sur la vie de l'entreprise" },
  { id: 'theme-5', code: 'MT', name: "Les mutations du travail" },
  { id: 'theme-6', code: 'CSE', name: "Les choix stratégiques de l'entreprise" },
  { id: 'methodo', code: 'METH', name: "Méthodologie" },
] as const;

export const CEJM_SOUS_MATIERES = ['Economie', 'Droit', 'Management'] as const;

export const GO_CHAPITRES = [
  { id: 'chapitre-1', number: 1, name: "Le cycle d'exploitation" },
  { id: 'chapitre-2', number: 2, name: "Les documents commerciaux et opérations de règlement" },
  { id: 'chapitre-3', number: 3, name: "La gestion des stocks" },
  { id: 'chapitre-4', number: 4, name: "La gestion des approvisionnements" },
  { id: 'chapitre-5', number: 5, name: "Le financement de l'exploitation" },
  { id: 'chapitre-6', number: 6, name: "Le compte de résultat" },
  { id: 'chapitre-7', number: 7, name: "Le coût de revient" },
  { id: 'chapitre-8', number: 8, name: "Le seuil de rentabilité" },
  { id: 'chapitre-9', number: 9, name: "Fixer les prix" },
  { id: 'chapitre-10', number: 10, name: "Le bilan" },
  { id: 'chapitre-11', number: 11, name: "La prévision des ventes" },
  { id: 'chapitre-12', number: 12, name: "Les budgets" },
  { id: 'chapitre-13', number: 13, name: "Le choix d'investissement" },
  { id: 'chapitre-14', number: 14, name: "Le financement de l'investissement" },
  { id: 'chapitre-15', number: 15, name: "Le tableau de bord" },
] as const;

// Helpers
export const getThemeById = (id: string) => CEJM_THEMES.find(t => t.id === id);
export const getChapitreById = (id: string) => GO_CHAPITRES.find(c => c.id === id);
export const getAvailableSousMatieres = (themeId: string) => {
  if (themeId === 'theme-6') return ['Management'] as const;
  return CEJM_SOUS_MATIERES;
};

// ============================================
// 🪝 HOOK PRINCIPAL (avec cache mémoire instantané)
// ============================================

export function useProfKylianContent() {
  // ✅ Initialiser avec le cache mémoire (SYNCHRONE = pas de spinner)
  const cached = getMemoryCache();
  
  const [fiches, setFiches] = useState<FicheItem[]>(cached?.fiches || []);
  const [videos, setVideos] = useState<VideoItem[]>(cached?.videos || []);
  const [quiz, setQuiz] = useState<QuizItem[]>(cached?.quiz || []);
  const [examens, setExamens] = useState<ExamenItem[]>(cached?.examens || []);
  
  // ✅ Si cache mémoire existe, pas de loading initial !
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  const loadContent = useCallback(async () => {
    try {
      setError(null);

      // 1. Si pas de cache mémoire, essayer AsyncStorage
      if (!memoryCache) {
        const asyncCached = await getAsyncCache();
        if (asyncCached) {
          console.log('[ProfKylian] ⚡ Restauration depuis AsyncStorage');
          setFiches(asyncCached.fiches);
          setVideos(asyncCached.videos);
          setQuiz(asyncCached.quiz);
          setExamens(asyncCached.examens);
          setMemoryCache(asyncCached); // Mettre en mémoire pour la suite
          setLoading(false);
          
          // Si cache frais, ne pas recharger
          if (Date.now() - asyncCached.timestamp < CACHE_DURATION) {
            return;
          }
        }
      } else {
        // Cache mémoire existe et frais
        if (Date.now() - memoryCache.timestamp < CACHE_DURATION) {
          console.log('[ProfKylian] ✅ Cache mémoire valide, skip fetch');
          setLoading(false);
          return;
        }
      }

      // 2. Charger depuis Supabase (en arrière-plan si cache existe)
      console.log('[ProfKylian] 🌐 Chargement depuis Supabase');
      
      const [fichesRes, videosRes, quizRes, examensRes] = await Promise.all([
        supabase
          .from('prof_kylian_fiches')
          .select('*')
          .order('theme_id')
          .order('sous_matiere')
          .order('ordre'),
        supabase
          .from('prof_kylian_videos')
          .select('*')
          .order('ordre'),
        supabase
          .from('prof_kylian_quiz')
          .select('*')
          .order('ordre'),
        supabase
          .from('prof_kylian_examens')
          .select('*')
          .order('year', { ascending: false }),
      ]);

      if (fichesRes.error) throw fichesRes.error;
      if (videosRes.error) throw videosRes.error;
      if (quizRes.error) throw quizRes.error;
      if (examensRes.error) throw examensRes.error;

      const newFiches = fichesRes.data || [];
      const newVideos = videosRes.data || [];
      const newQuiz = quizRes.data || [];
      const newExamens = examensRes.data || [];

      // Mettre à jour l'état
      setFiches(newFiches);
      setVideos(newVideos);
      setQuiz(newQuiz);
      setExamens(newExamens);

      // Sauvegarder dans les deux caches
      setMemoryCache({
        fiches: newFiches,
        videos: newVideos,
        quiz: newQuiz,
        examens: newExamens,
      });
      
      await setAsyncCache({
        fiches: newFiches,
        videos: newVideos,
        quiz: newQuiz,
        examens: newExamens,
      });

    } catch (err) {
      console.error('[useProfKylianContent] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  // Force refresh (ignore le cache)
  const forceRefresh = useCallback(async () => {
    setLoading(true);
    try {
      const [fichesRes, videosRes, quizRes, examensRes] = await Promise.all([
        supabase
          .from('prof_kylian_fiches')
          .select('*')
          .order('theme_id')
          .order('sous_matiere')
          .order('ordre'),
        supabase
          .from('prof_kylian_videos')
          .select('*')
          .order('ordre'),
        supabase
          .from('prof_kylian_quiz')
          .select('*')
          .order('ordre'),
        supabase
          .from('prof_kylian_examens')
          .select('*')
          .order('year', { ascending: false }),
      ]);

      if (fichesRes.error) throw fichesRes.error;
      if (videosRes.error) throw videosRes.error;
      if (quizRes.error) throw quizRes.error;
      if (examensRes.error) throw examensRes.error;

      const newFiches = fichesRes.data || [];
      const newVideos = videosRes.data || [];
      const newQuiz = quizRes.data || [];
      const newExamens = examensRes.data || [];

      setFiches(newFiches);
      setVideos(newVideos);
      setQuiz(newQuiz);
      setExamens(newExamens);

      setMemoryCache({
        fiches: newFiches,
        videos: newVideos,
        quiz: newQuiz,
        examens: newExamens,
      });

      await setAsyncCache({
        fiches: newFiches,
        videos: newVideos,
        quiz: newQuiz,
        examens: newExamens,
      });
    } catch (err) {
      console.error('[forceRefresh] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const filterFiches = useCallback((params: {
    matiere?: Matiere | 'all';
    themeId?: string | 'all';
    sousMatiere?: CEJMSousMatiere | 'all';
    chapitreId?: string | 'all';
  }) => {
    return fiches.filter(fiche => {
      if (params.matiere && params.matiere !== 'all' && fiche.matiere !== params.matiere) return false;
      if (params.themeId && params.themeId !== 'all' && fiche.theme_id !== params.themeId) return false;
      if (params.sousMatiere && params.sousMatiere !== 'all' && fiche.sous_matiere !== params.sousMatiere) return false;
      if (params.chapitreId && params.chapitreId !== 'all' && fiche.chapitre_id !== params.chapitreId) return false;
      return true;
    });
  }, [fiches]);

  const filterVideos = useCallback((params: {
    matiere?: Matiere | 'all';
    themeId?: string | 'all';
    chapitreId?: string | 'all';
  }) => {
    return videos.filter(video => {
      if (params.matiere && params.matiere !== 'all' && video.matiere !== params.matiere) return false;
      if (params.themeId && params.themeId !== 'all' && video.theme_id !== params.themeId) return false;
      if (params.chapitreId && params.chapitreId !== 'all' && video.chapitre_id !== params.chapitreId) return false;
      return true;
    });
  }, [videos]);

  const filterQuiz = useCallback((params: {
    matiere?: Matiere | 'all';
    themeId?: string | 'all';
    sousMatiere?: CEJMSousMatiere | 'all';
    chapitreId?: string | 'all';
  }) => {
    return quiz.filter(q => {
      if (params.matiere && params.matiere !== 'all' && q.matiere !== params.matiere) return false;
      if (params.themeId && params.themeId !== 'all' && q.theme_id !== params.themeId) return false;
      if (params.sousMatiere && params.sousMatiere !== 'all' && q.sous_matiere !== params.sousMatiere) return false;
      if (params.chapitreId && params.chapitreId !== 'all' && q.chapitre_id !== params.chapitreId) return false;
      return true;
    });
  }, [quiz]);

  const getFicheById = useCallback((id: string) => {
    return fiches.find(f => f.id === id);
  }, [fiches]);

  const stats = {
    totalFiches: fiches.length,
    totalVideos: videos.length,
    totalQuiz: quiz.length,
    totalExamens: examens.length,
    totalContent: fiches.length + videos.length + quiz.length + examens.length,
    freeFiches: fiches.filter(f => f.is_free).length,
    freeVideos: videos.filter(v => v.is_free).length,
    freeQuiz: quiz.filter(q => q.is_free).length,
    freeExamens: examens.filter(e => e.is_free).length,
  };

  return {
    fiches,
    videos,
    quiz,
    examens,
    loading,
    error,
    refetch: loadContent,
    forceRefresh,
    filterFiches,
    filterVideos,
    filterQuiz,
    getFicheById,
    stats,
  };
}

// ============================================
// 🪝 HOOK POUR UNE FICHE SEULE
// ============================================

export function useProfKylianFiche(ficheId: string) {
  const [fiche, setFiche] = useState<FicheItem | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFiche = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: ficheError } = await supabase
          .from('prof_kylian_fiches')
          .select('*')
          .eq('id', ficheId)
          .single();

        if (ficheError) throw ficheError;
        if (!data) throw new Error('Fiche non trouvée');

        setFiche(data);

        const response = await fetch(data.markdown_url);
        if (!response.ok) throw new Error('Erreur de chargement du contenu');
        
        const markdown = await response.text();
        setMarkdownContent(markdown);
      } catch (err) {
        console.error('[useProfKylianFiche] Error:', err);
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };

    if (ficheId) {
      fetchFiche();
    }
  }, [ficheId]);

  return { fiche, markdownContent, loading, error };
}
